import { allChallenges } from "@mono/allChallenges"
import { featureFlags } from "@mono/constants"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import backgroundImage from "@/images/backgrounds/lobby-background.webp"
import { useAppSelector } from "@/store/hooks"
import Navbar from "@/Components/Navbar"
import ChallengeItem from "./Challenges/ChallengeItem"
import ChallengeDetail from "./Challenges/ChallengeDetail"
import { SAFE_OVERLAY_PADDING, SAFE_OVERLAY_PADDING_SM, SAFE_PAGE_FRAME } from "@/utils/safeArea"

const isChallengeLocked = (challenge: IChallenge) => !featureFlags.allowedChallenges.includes(challenge.id)

export default function ChallengesList() {
  const nav = useNavigate()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false)
  const totalChallenges = allChallenges.length
  // Only a few missions are playable right now; the rest sit on the board as
  // "Coming Soon", so progress is measured against what can actually be played.
  const playableChallenges = allChallenges.filter((challenge) => !isChallengeLocked(challenge))

  const completedChallenges = useAppSelector((state) => state.global.userData?.completedChallenges) ?? []
  const completedCount = playableChallenges.filter((challenge) => completedChallenges.includes(challenge.id)).length
  const completionPercent =
    playableChallenges.length > 0 ? Math.round((completedCount / playableChallenges.length) * 100) : 0

  const selectedChallenge = allChallenges[selectedIndex]

  const handleSelect = (index: number) => {
    setSelectedIndex(index)
    setMobileDetailOpen(true)
  }

  const handlePlay = () => {
    // Placeholder action until challenge gameplay flow is wired.
    setMobileDetailOpen(false)
  }

  return (
    <div className="relative w-screen h-screen bg-cover flex flex-col" style={{ backgroundImage: `url(${backgroundImage})` }}>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/75 pointer-events-none" />
      <Navbar />

      <div className={`relative z-10 flex-1 min-h-0 flex flex-col ${SAFE_PAGE_FRAME}`}>
        <div className="w-full max-w-[110rem] mx-auto flex flex-col flex-1 min-h-0">
          {/* Header */}
          <div className="flex items-start mobile:items-center justify-between gap-4 mobile:gap-[10px] mb-5 sm:mb-3 mobile:mb-[10px]">
            <div className="mobile:flex mobile:min-w-0 mobile:items-center mobile:gap-[10px]">
              <p className="mobile:hidden text-xs font-semibold uppercase tracking-wider text-amber-400/70 mb-1.5">Campaign Board</p>
              <h1 className="font-fourth text-3xl sm:text-2xl mobile:text-[24px]/[1.15] font-bold tracking-wide text-white">Challenges</h1>
              <div className="mt-2.5 mobile:mt-0 flex items-center gap-3 mobile:gap-[8px]">
                <span className="inline-flex items-center rounded-lg mobile:rounded-[8px] border border-amber-400/30 bg-amber-500/10 px-2.5 py-1 mobile:px-[9px] mobile:py-[3px] text-amber-200 text-xs mobile:text-[11px]/[1.4] font-semibold">
                  {playableChallenges.length} of {totalChallenges} Missions
                </span>
                <span className="mobile:hidden text-sm sm:text-xs text-white/50">Pick a mission and launch your run. More coming soon.</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => nav("/lobby")}
              className="flex flex-shrink-0 items-center gap-2 mobile:gap-[6px] px-5 py-2.5 sm:px-3 sm:py-2 mobile:px-[12px] mobile:py-[8px] rounded-xl mobile:rounded-[12px] border-2 border-amber-400/70 bg-amber-500/15 text-amber-100 font-fourth font-bold tracking-wide mobile:text-[13px]/[1.2] shadow-lg shadow-amber-500/20 hover:bg-amber-500/25 hover:border-amber-300 hover:scale-[1.03] active:scale-100 transition-all"
            >
              <i className="fas fa-arrow-left text-sm mobile:text-[12px]" />
              Back to Lobby
            </button>
          </div>

          {/* Progress */}
          <div className="mb-5 sm:mb-3 mobile:mb-[10px] rounded-2xl mobile:rounded-[14px] border border-white/10 bg-white/[0.03] px-4 py-3 sm:px-3 sm:py-2.5 mobile:px-[12px] mobile:py-[8px]">
            <div className="flex items-center justify-between mb-2 mobile:mb-[5px]">
              <span className="text-xs mobile:text-[11px]/[1.4] font-semibold uppercase tracking-wider text-amber-400/70">Progress</span>
              <span className="text-sm sm:text-xs mobile:text-[12px]/[1.4] font-semibold text-white/80">
                {completedCount} / {playableChallenges.length} completed
                <span className="ml-2 text-amber-300">{completionPercent}%</span>
              </span>
            </div>
            <div className="h-2.5 mobile:h-[8px] w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all duration-500"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
          </div>

          {/* Master / detail */}
          <div className="flex flex-1 min-h-0 gap-5 sm:gap-3 mobile:gap-[10px] flex-col md:flex-row">
            {/* List */}
            <div className="flex flex-col min-h-0 md:w-[440px] xl:w-[520px] rounded-2xl mobile:rounded-[16px] border border-white/10 bg-gradient-to-b from-gray-900 via-gray-950 to-black p-2.5 mobile:p-[8px]">
              <div className="challenge-scroll flex-1 overflow-y-auto flex flex-col gap-2 mobile:gap-[8px] pr-1.5 mobile:pr-[6px]">
                {allChallenges.map((challenge, index) => (
                  <ChallengeItem
                    key={`${challenge.id}-${index}`}
                    challenge={challenge}
                    index={index}
                    isSelected={index === selectedIndex}
                    locked={isChallengeLocked(challenge)}
                    completed={completedChallenges.includes(challenge.id)}
                    onClick={handleSelect}
                  />
                ))}
              </div>
            </div>

            {/* Detail (desktop) */}
            <div className="hidden md:flex flex-1 min-h-0">
              {selectedChallenge ? (
                <ChallengeDetail
                  challenge={selectedChallenge}
                  index={selectedIndex}
                  locked={isChallengeLocked(selectedChallenge)}
                  completed={completedChallenges.includes(selectedChallenge.id)}
                  onPlay={handlePlay}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Detail (mobile overlay) */}
      {mobileDetailOpen && selectedChallenge ? (
        <div
          className={`md:hidden fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center ${SAFE_OVERLAY_PADDING} ${SAFE_OVERLAY_PADDING_SM}`}
        >
          <div className="relative w-full max-w-lg mobile:max-w-[520px] max-h-full">
            <button
              type="button"
              onClick={() => setMobileDetailOpen(false)}
              aria-label="Close"
              className="absolute -top-2 -right-2 mobile:-top-[8px] mobile:-right-[8px] z-10 flex h-9 w-9 mobile:h-[34px] mobile:w-[34px] items-center justify-center rounded-full border border-white/15 bg-gray-950 text-white/70 hover:text-white text-lg mobile:text-[22px]/[1] font-bold"
            >
              &times;
            </button>
            <ChallengeDetail
              challenge={selectedChallenge}
              index={selectedIndex}
              locked={isChallengeLocked(selectedChallenge)}
              completed={completedChallenges.includes(selectedChallenge.id)}
              onPlay={handlePlay}
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}
